import type { LogSet } from "./schemas/logSet.js";
import type { ExerciseResult } from "./schemas/sessionResult.js";
import { EQUIPMENT_INSTANCE_NS, readTagValue } from "./schemas/tag.js";
import { groupSetsByEntry } from "./workoutAssembly.js";

// A session read set by set (#25, the coach's view of an athlete's session):
// one group per exercise entry, in the order the athlete did them, each set
// flagged when it is the one that made the session's e1RM record.

export type SessionSetRow = { set: LogSet; record: boolean };

export type SessionSetGroup = {
  key: string;
  exerciseSlug: string;
  /** Write-time snapshot, only a fallback for a deleted catalog row. */
  exerciseName?: string;
  equipmentInstance?: string;
  rows: SessionSetRow[];
};

// A tolerance keeps float drift between the stored top set and the logged
// set from hiding the mark.
const sameKg = (a: number, b: number) => Math.abs(a - b) < 0.01;

/** The set behind an exercise's e1RM record: the first clean set at the
 *  stored top weight and reps. null when the result names no record. */
const recordSetOf = (
  members: readonly LogSet[],
  result: ExerciseResult | undefined,
): LogSet | null => {
  const top = result?.isPrE1rm ? result.topSet : null;
  if (!top) return null;
  return (
    members.find(
      (s) =>
        !s.fail &&
        s.reps === top.reps &&
        s.weight !== undefined &&
        sameKg(s.weight, top.weightKg),
    ) ?? null
  );
};

export function sessionSetGroups(
  sets: readonly LogSet[],
  exerciseResults: readonly ExerciseResult[] = [],
): SessionSetGroup[] {
  const groups: SessionSetGroup[] = [];
  const marked = new Set<string>();
  for (const [key, members] of groupSetsByEntry(sets)) {
    const first = members[0]!;
    const equipmentInstance = readTagValue(first.tags, EQUIPMENT_INSTANCE_NS);
    const resultKey = `${first.exerciseSlug}\u0000${equipmentInstance ?? ""}`;
    // One record mark per exercise, even when it was done in two entries.
    const record = marked.has(resultKey)
      ? null
      : recordSetOf(
          members,
          exerciseResults.find(
            (r) =>
              r.exerciseSlug === first.exerciseSlug &&
              (r.equipmentInstance ?? "") === (equipmentInstance ?? ""),
          ),
        );
    if (record) marked.add(resultKey);
    groups.push({
      key,
      exerciseSlug: first.exerciseSlug,
      ...(first.exerciseName ? { exerciseName: first.exerciseName } : {}),
      ...(equipmentInstance ? { equipmentInstance } : {}),
      rows: members.map((set) => ({ set, record: set === record })),
    });
  }
  return groups;
}
